import type { Coding } from '@medplum/fhirtypes'
import { invariant } from '#src/invariant.ts'
import { searchTerms } from '#src/searchTerms.ts'
import { COMMON_OBSERVATION_CODES } from '#src/terminology/commonObservations.ts'
import { AD8_TEMPLATE } from '#src/terminology/instruments/ad8.ts'
import { ADL_TEMPLATE } from '#src/terminology/instruments/adl.ts'
import { BRADEN_TEMPLATE } from '#src/terminology/instruments/braden.ts'
import { GAD2_TEMPLATE } from '#src/terminology/instruments/gad2.ts'
import { GAD7_TEMPLATE } from '#src/terminology/instruments/gad7.ts'
import { IADL_TEMPLATE } from '#src/terminology/instruments/iadl.ts'
import { MINI_COG_TEMPLATE } from '#src/terminology/instruments/miniCog.ts'
import { MMSE_TEMPLATE } from '#src/terminology/instruments/mmse.ts'
import { MOCA_TEMPLATE } from '#src/terminology/instruments/moca.ts'
import { PHQ2_TEMPLATE } from '#src/terminology/instruments/phq2.ts'
import { PHQ9_TEMPLATE } from '#src/terminology/instruments/phq9.ts'
import { SLUMS_TEMPLATE } from '#src/terminology/instruments/slums.ts'
import { STEADI_TEMPLATE } from '#src/terminology/instruments/steadi.ts'
import type { InstrumentTemplate } from '#src/terminology/instruments/types.ts'

// What clinicians type for an observation ("bp", "wt", "a1c", "phq9") and the
// code they mean. An alias typed in full ranks its code first.
export interface ObservationShorthand {
  aliases: readonly string[]
  coding: Coding
  // Set when the alias names an instrument, whose panel `coding` is.
  instrument?: string
  // The short label a chart shows for this code in place of its display.
  nickname?: string
}

function common(
  code: string,
  aliases: readonly string[],
  nickname?: string
): ObservationShorthand {
  const coding = COMMON_OBSERVATION_CODES.find((entry) => entry.code === code)
  invariant(coding, `${code} is not a common observation code`)
  const entry: ObservationShorthand = { aliases, coding }
  if (nickname) {
    entry.nickname = nickname
  }
  return entry
}

function instrument(
  template: InstrumentTemplate,
  aliases: readonly string[]
): ObservationShorthand {
  return {
    aliases,
    coding: { ...template.panel, display: template.title },
    instrument: template.id,
  }
}

// An instrument comes ahead of its total score under the same alias: the
// instrument records the total too.
export const OBSERVATION_SHORTHAND: readonly ObservationShorthand[] = [
  common('85354-9', ['bp', 'b/p', 'blood pressure']),
  common('8480-6', ['sbp', 'systolic', 'systolic bp']),
  common('8462-4', ['dbp', 'diastolic', 'diastolic bp']),
  common('8478-0', ['map', 'mean arterial pressure', 'mean bp']),
  common('8867-4', ['hr', 'pulse', 'heart rate']),
  common('9279-1', ['rr', 'resp', 'resp rate', 'respirations']),
  common('8310-5', ['temp', 'temperature', 'fever']),
  common('20564-1', ['spo2', 'o2 sat', 'o2 sats', 'sat', 'sats', 'pulse ox']),
  common('29463-7', ['wt', 'weight']),
  common('8302-2', ['ht', 'height', 'stature']),
  common('8287-5', ['hc', 'ofc', 'head circumference']),
  common('39156-5', ['bmi']),
  common('8277-6', ['bsa']),
  common('38208-5', ['pain', 'pain score', 'pain scale']),
  common(
    '4548-4',
    ['a1c', 'hba1c', 'hgb a1c', 'hemoglobin a1c', 'glycated hemoglobin'],
    'A1c'
  ),
  common('2339-0', ['glucose', 'glu', 'blood sugar', 'bg', 'fsbs']),
  common('1558-6', ['fasting glucose', 'fbg', 'fasting blood sugar']),
  common('41653-7', [
    'fingerstick',
    'fingerstick glucose',
    'glucometer',
    'poc glucose',
  ]),
  common('2160-0', ['creatinine', 'cr', 'creat']),
  common('62238-1', ['egfr', 'gfr']),
  common('3094-0', ['bun', 'urea']),
  common('2951-2', ['na', 'sodium']),
  common('2823-3', ['k', 'potassium']),
  common('2075-0', ['cl', 'chloride']),
  common('2028-9', ['co2', 'bicarb', 'bicarbonate']),
  common('17861-6', ['ca', 'calcium']),
  common('19123-9', ['mg', 'magnesium']),
  common('2777-1', ['phos', 'phosphate', 'phosphorus']),
  common('1742-6', ['alt', 'sgpt']),
  common('1920-8', ['ast', 'sgot']),
  common('6768-6', ['alp', 'alk phos', 'alkaline phosphatase']),
  common('1975-2', ['bili', 'tbili', 'bilirubin']),
  common('1751-7', ['alb', 'albumin']),
  common('6301-6', ['inr']),
  common('1988-5', ['crp', 'c-reactive protein']),
  common('2276-4', ['ferritin']),
  common('1989-3', ['vitamin d', 'vit d', '25-oh']),
  common('718-7', ['hgb', 'hb', 'hemoglobin']),
  common('4544-3', ['hct', 'hematocrit']),
  common('6690-2', ['wbc', 'white count', 'leukocytes']),
  common('777-3', ['plt', 'platelets']),
  common('2093-3', ['chol', 'cholesterol', 'total cholesterol']),
  common('2085-9', ['hdl']),
  common('13457-7', ['ldl']),
  common('2571-8', ['tg', 'trig', 'triglycerides']),
  common('3016-3', ['tsh', 'thyroid']),
  common('2132-9', ['b12', 'vitamin b12']),
  common('14959-1', ['uacr', 'microalbumin', 'albumin creatinine ratio']),
  instrument(PHQ9_TEMPLATE, ['phq', 'phq9', 'phq-9', 'depression screen']),
  common('44261-6', ['phq', 'phq9', 'phq-9', 'phq9 score', 'phq9 total']),
  instrument(PHQ2_TEMPLATE, ['phq2', 'phq-2']),
  common('55758-7', ['phq2', 'phq-2', 'phq2 score']),
  instrument(GAD7_TEMPLATE, ['gad', 'gad7', 'gad-7', 'anxiety screen']),
  common('70274-6', ['gad', 'gad7', 'gad-7', 'gad7 score']),
  instrument(GAD2_TEMPLATE, ['gad2', 'gad-2']),
  instrument(MINI_COG_TEMPLATE, ['minicog', 'mini cog', 'mini-cog']),
  instrument(AD8_TEMPLATE, ['ad8', 'ad-8', 'ascertain dementia']),
  instrument(STEADI_TEMPLATE, ['steadi', 'stay independent']),
  instrument(ADL_TEMPLATE, ['adl', 'adls', 'katz']),
  instrument(IADL_TEMPLATE, ['iadl', 'iadls', 'lawton']),
  instrument(BRADEN_TEMPLATE, [
    'braden',
    'braden scale',
    'pressure ulcer risk',
    'pressure injury risk',
  ]),
  instrument(MOCA_TEMPLATE, ['moca', 'montreal cognitive']),
  instrument(SLUMS_TEMPLATE, ['slums']),
  instrument(MMSE_TEMPLATE, ['mmse', 'mini mental']),
]

// One spelling of an alias: "PHQ-9", "phq 9" and "phq9" agree, as do "O2
// sat" and "o2sat".
function shorthandKey(text: string): string {
  return searchTerms(text).join('').replaceAll('-', '')
}

const BY_KEY = new Map<string, ObservationShorthand[]>()
for (const entry of OBSERVATION_SHORTHAND) {
  for (const key of new Set(entry.aliases.map(shorthandKey))) {
    BY_KEY.set(key, [...(BY_KEY.get(key) ?? []), entry])
  }
}

// The entries one of whose aliases is the search, in table order.
export function shorthandFor(input: string): ObservationShorthand[] {
  const key = shorthandKey(input)
  return key ? (BY_KEY.get(key) ?? []) : []
}

// What to ask the terminology server for an instrument's alias, which LOINC
// does not know: "phq9" reaches it as "patient health questionnaire", the
// instrument's name without its abbreviation.
export function shorthandServerSearch(input: string): string | undefined {
  const entry = shorthandFor(input).find((candidate) => candidate.instrument)
  const name = entry?.coding.display?.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
  return name ? searchTerms(name).join(' ') || undefined : undefined
}

const NICKNAME_BY_CODE = new Map(
  OBSERVATION_SHORTHAND.flatMap((entry) =>
    entry.nickname && entry.coding.code
      ? [[entry.coding.code, entry.nickname] as const]
      : []
  )
)

// The chart label the shorthand table gives a code, if any.
export function shorthandNickname(code: string): string | undefined {
  return NICKNAME_BY_CODE.get(code)
}
